"use client";

import { useState, useEffect, useRef } from "react";

export const SUPPORTED_CURRENCIES = ["LKR", "USD"] as const;
export type Currency = (typeof SUPPORTED_CURRENCIES)[number];

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
    USD: "$",
    LKR: "LKR ",
};

const LS_KEY = "preferred_currency";

function getStoredCurrency(): Currency {
    if (typeof window === "undefined") return "LKR";
    const stored = localStorage.getItem(LS_KEY);
    return (SUPPORTED_CURRENCIES as readonly string[]).includes(stored ?? "") ? (stored as Currency) : "LKR";
}

export function useCurrencyRate(currency: Currency) {
    const [rate, setRate] = useState<number>(1);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (currency === "LKR") {
            setRate(1);
            return;
        }
        setLoading(true);
        fetch("https://open.er-api.com/v6/latest/LKR")
            .then((r) => r.json())
            .then((data) => {
                if (data.rates?.[currency]) setRate(data.rates[currency]);
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [currency]);

    return { rate, loading, symbol: CURRENCY_SYMBOLS[currency] };
}

export function formatPrice(amount: number, currency: Currency): string {
    if (currency === "LKR") return amount.toLocaleString("en-US", { maximumFractionDigits: 0 });
    return amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function CurrencySelector({
    value,
    onChange,
}: {
    value: Currency;
    onChange: (c: Currency) => void;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // Restore from localStorage on mount
    useEffect(() => {
        const stored = getStoredCurrency();
        if (stored !== value) onChange(stored);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen((o) => !o)}
                className="backdrop-blur-xl border border-border/70 bg-white/5 rounded-full px-3 py-2 text-sm font-medium text-foreground flex items-center gap-2 transition-all hover:bg-white/10"
            >
                <span>{value}</span>
                <svg
                    className={`w-3 h-3 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                >
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>

            {open && (
                <div className="absolute right-0 top-full mt-2 z-50 min-w-[110px] rounded-xl border border-border/70 bg-black/30 backdrop-blur-xl overflow-hidden shadow-xl">
                    {SUPPORTED_CURRENCIES.map((c) => (
                        <button
                            key={c}
                            onClick={() => { localStorage.setItem(LS_KEY, c); onChange(c); setOpen(false); }}
                            className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium transition-colors hover:bg-white/5 ${value === c ? "text-foreground" : "text-muted-foreground"}`}
                        >
                            <span>{c}</span>
                            {value === c && (
                                <svg className="ml-auto w-3.5 h-3.5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
